import React, { createContext, useState, useEffect } from 'react';

export const ProductContext = createContext();

const sortProducts = (productList) =>
  [...productList].sort((a, b) => {
    const aOrder = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const bOrder = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    return Number(a.id) - Number(b.id);
  });

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const API_BASE = '/api';

  useEffect(() => {
    fetch(`${API_BASE}/products`)
      .then((res) => res.json())
      .then((data) => setProducts(sortProducts(data)))
      .catch((error) => console.error('Failed to load products', error));
  }, []);

  const addProduct = async (product) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('You must be logged in as an admin to add products.');
    }

    const payload = {
      ...product,
      price: parseFloat(product.price) || 0,
      sortOrder:
        product.sortOrder === '' || product.sortOrder === null || product.sortOrder === undefined
          ? null
          : parseInt(product.sortOrder, 10),
    };

    const res = await fetch(`${API_BASE}/products`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': token,
      },
      body: JSON.stringify(payload),
    });

    const newProduct = await res.json();

    if (!res.ok) {
      throw new Error(newProduct.msg || 'Failed to add product');
    }

    setProducts((prev) => sortProducts([...prev, newProduct]));
  };

  const updateProduct = async (updatedProduct) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('You must be logged in as an admin to update products.');
    }

    const payload = {
      ...updatedProduct,
      price: parseFloat(updatedProduct.price) || 0,
      sortOrder:
        updatedProduct.sortOrder === '' ||
        updatedProduct.sortOrder === null ||
        updatedProduct.sortOrder === undefined
          ? null
          : parseInt(updatedProduct.sortOrder, 10),
    };

    const res = await fetch(`${API_BASE}/products/${updatedProduct.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': token,
      },
      body: JSON.stringify(payload),
    });

    const updated = await res.json();

    if (!res.ok) {
      throw new Error(updated.msg || 'Failed to update product');
    }

    setProducts((prev) => sortProducts(prev.map((p) => (p.id === updated.id ? updated : p))));
  };

  const reorderProducts = async (orderedProducts) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('You must be logged in as an admin to reorder products.');
    }

    const res = await fetch(`${API_BASE}/products/order`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': token,
      },
      body: JSON.stringify({ productIds: orderedProducts.map((product) => product.id) }),
    });

    const reorderedProducts = await res.json();

    if (!res.ok) {
      throw new Error(reorderedProducts.msg || 'Failed to save product order');
    }

    setProducts(sortProducts(reorderedProducts));
    return reorderedProducts;
  };

  const deleteProduct = async (productId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('You must be logged in as an admin to delete products.');
    }

    const res = await fetch(`${API_BASE}/products/${productId}`, {
      method: 'DELETE',
      headers: {
        'x-auth-token': token,
      },
    });

    if (!res.ok) {
      const raw = await res.text();
      let message = 'Failed to delete product';
      if (raw) {
        try {
          const data = JSON.parse(raw);
          message = data.msg || message;
        } catch (_error) {
          message = raw;
        }
      }
      throw new Error(message);
    }

    setProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const toggleFeatured = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      await updateProduct({ ...product, featured: !product.featured });
    }
  };

  return (
    <ProductContext.Provider value={{ products, addProduct, updateProduct, reorderProducts, deleteProduct, toggleFeatured }}>
      {children}
    </ProductContext.Provider>
  );
};
